'use server';
import { actionErrorMessage } from '@/shared/infrastructure/logging/actionError';
import type { ActionResult } from '@/shared/types/actions';
import { SessionService } from '@/contexts/authentication';
import { walletAccountId } from '@/contexts/subscriptions/services/walletAccountId';
import type { StoryBrief } from '@/contexts/media/realtimeVoice';

/**
 * Turn a raw dictation transcript (which may be a young child rambling) into a
 * structured story brief with ONE cheap LLM pass: it tidies the idea into a clean
 * synopsis and extracts what it can (genre/age/length/heroes). The wizard folds
 * the result through mapBriefToParameters, so the parent lands on a prefilled,
 * fully-editable form. Best-effort — on any failure the caller just uses the raw
 * transcript as the prompt.
 */
const SYSTEM = [
  'You turn a spoken story idea from a parent or a young child into a compact JSON brief.',
  'The speech may ramble, be ungrammatical, or mix languages — infer intent charitably; never invent a whole new plot.',
  'Return ONLY a JSON object (no prose) with these optional keys:',
  '  synopsis: string — a clean 1-3 sentence retelling of THEIR idea, in the SAME language they spoke.',
  '  title: string — only if they named one.',
  '  genre: one of "lullaby","adventure","comedy","fable","mystery","everyday","educational","classic".',
  '  ageGroup: one of "3-5","5-7","7-10","10+".',
  '  energy: integer 1..5.',
  '  desiredDurationMinutes: integer.',
  '  characters: array of { name: string, description?: string } for heroes they named.',
  'Omit any key you are not confident about. synopsis is required.',
].join('\n');

function coerce(raw: unknown): StoryBrief | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const synopsis = typeof o.synopsis === 'string' ? o.synopsis.trim() : '';
  if (!synopsis) return null;
  const str = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : undefined);
  const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : undefined);
  const characters = Array.isArray(o.characters)
    ? o.characters
        .map((c) => {
          const co = (c ?? {}) as Record<string, unknown>;
          const name = typeof co.name === 'string' ? co.name.trim() : '';
          if (!name) return null;
          const description = str(co.description);
          return description ? { name, description } : { name };
        })
        .filter((c): c is { name: string; description?: string } => c !== null)
    : undefined;
  return {
    synopsis,
    title: str(o.title),
    genre: str(o.genre),
    ageGroup: str(o.ageGroup),
    energy: num(o.energy),
    desiredDurationMinutes: num(o.desiredDurationMinutes),
    characters: characters && characters.length ? characters : undefined,
  };
}

export async function refineDictation(
  transcript: string,
  locale: string,
): Promise<ActionResult<StoryBrief>> {
  try {
    const familyId = await SessionService.getFamilyId();
    const userId = await SessionService.getUserId();
    if (!familyId || !userId) return { success: false, error: 'Not authenticated' };
    const text = (transcript || '').trim();
    if (text.length < 3) return { success: false, error: 'Empty transcript' };

    const { UnifiedAIConfigService } = await import('@/contexts/ai/services/config/UnifiedAIConfigService');
    const { getTokenManager } = await import('@/contexts/subscriptions/services/getTokenManager');
    const { buildLlmMessagesAndParams, toWorkerLlmProvider } = await import('@/contexts/ai/services/oneShotTask');
    const { runLlmGenerate } = await import('@/shared/infrastructure/workflows/llmRun');
    const { billedFailuresOf, trackBilledFailuresCovered } = await import('@/shared/infrastructure/workflows/billedFailures');

    const estimatedCost = await UnifiedAIConfigService.getOperationReservation('block_regeneration');
    const tokenManager = getTokenManager();
    const reservation = await tokenManager.reserve({
      familyId,
      accountId: await walletAccountId(familyId),
      amount: estimatedCost,
      operationType: 'dictation_refine',
      metadata: { createdBy: userId },
    });

    try {
      const model = await UnifiedAIConfigService.getModelForCategory('llm');
      const provider = toWorkerLlmProvider((await UnifiedAIConfigService.getConfig()).llm?.provider);
      const { messages, params } = buildLlmMessagesAndParams({
        system: SYSTEM,
        user: `Language: ${locale}\nDictation:\n"""${text}"""`,
        model,
        maxTokens: 700,
      });
      const out = await runLlmGenerate({
        startKey: `dictation-refine-${familyId}-${Date.now()}`,
        messages,
        params: { ...params, response_format: { type: 'json_object' } },
        model,
        provider,
        familyId,
      });

      // Settle through the price-book (raw LLM usage → accounts prices → cost_facts +
      // correct cost-based credits), NOT legacy token math. Best-effort: a billing
      // hiccup must not discard a good brief (TTL sweep refunds a leaked hold).
      const promptTokens = Number(out.usage?.inputTokens ?? 0);
      const completionTokens = Number(out.usage?.outputTokens ?? 0);
      try {
        // charged provider failures inside the run (a Gemini block) → covered COGS, before settle
        await trackBilledFailuresCovered(tokenManager, reservation.id, out.billedFailures);
        await tokenManager.trackOperation({
          reservationId: reservation.id,
          type: 'text_generation',
          llmUsage: { promptTokens, completionTokens, totalTokens: promptTokens + completionTokens, model },
        });
        await tokenManager.settle({ reservationId: reservation.id });
      } catch {
        /* best-effort billing; leaked hold is TTL-swept */
      }

      let parsed: unknown;
      try {
        parsed = JSON.parse(out.text);
      } catch {
        const m = out.text.match(/\{[\s\S]*\}/);
        parsed = m ? JSON.parse(m[0]) : null;
      }
      const brief = coerce(parsed);
      if (!brief) return { success: false, error: 'Could not parse brief' };
      return { success: true, data: brief };
    } catch (e) {
      await trackBilledFailuresCovered(tokenManager, reservation.id, billedFailuresOf(e));
      await tokenManager.refund({ reservationId: reservation.id, reason: `dictation refine failed: ${e}` }).catch(() => {});
      throw e;
    }
  } catch (e: unknown) {
    return { success: false, error: actionErrorMessage('refineDictation', e) };
  }
}
